import BaseSiteFactorData from './BaseSiteFactorData'

import * as turf from '@turf/turf'

import GIF from 'gif.js'
import html2canvas from 'html2canvas'

// const O_TIMES = 0.1
const GIF_SIZE_RATIO = 1

const canvas = document.createElement('canvas')

export default {
  extends: BaseSiteFactorData,
  data: () => ({
    displayFactorId: null,
    displayValueKey: '',
    dataMap: Object.freeze({}),
    dataIndex: -1
  }),
  computed: {
    system: () => process.env.SYSTEM_NAME,
    defaultZoom: () => process.env.HOME_MIN_ZOOM,
    formatFilterSites () {
      const {filterSites, profileSiteMap} = this
      return filterSites.map(site => {
        const _site = profileSiteMap[site.id]
        return {
          id: site.id,
          name: site.name,
          lngLat: [_site.longitude, _site.latitude]
        }
      })
    },
    viewRect () {
      const {filterSites, profileSiteMap} = this

      if (filterSites.length > 0) {
        const siteLngLats = filterSites.map(site => {
          const _site = profileSiteMap[site.id]
          return [_site.longitude, _site.latitude]
        })
        const line = turf.lineString(siteLngLats)
        return turf.bbox(line)
      } else {
        return null
      }
    },
    boundaries () {
      return turf.flatten({
        type: 'FeatureCollection',
        name: 'boundaries',
        crs: {
          type: 'name',
          properties: {
            name: 'urn:ogc:def:crs:OGC:1.3:CRS84'
          }
        },
        features: this.system.boundaries.map(boundaries => ({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'MultiPolygon',
            coordinates: [[boundaries]]
          }
        }))
      })
    },
    AQI_LEVEL_MAP: () => {},
    colors: () => ['#00e400', '#ffff00', '#ff7e00', '#ff0000', '#99004c', '#7e0023'],
    _granularitys: () => [{
      id: 1,
      value: 'hour',
      step: 1,
      unit: 'Hours',
      complete: '',
      formatter: 'YYYY/MM/DD HH:mm:ss',
      displayFormatter: 'YYYY年MM月DD日HH时'
    }, {
      id: 24,
      value: 'day',
      step: 1,
      unit: 'Date',
      complete: '',
      formatter: 'YYYY/MM/DD HH:mm:ss',
      displayFormatter: 'YYYY年MM月DD日'
    }],
    granularitys () {
      return this._granularitys
    },
    granularityMap () {
      return this.granularitys.reduce((map, granularity) => {
        map[granularity.value] = granularity
        return map
      }, {})
    },
    granularityIdMap () {
      return this.granularitys.reduce((map, granularity) => {
        map[granularity.id] = granularity
        return map
      }, {})
    },
    currentGranularity () {
      return this.granularityIdMap[this.currentQuery.granularity]
    },
    granularityValues () {
      return this.granularitys.map(granularity => granularity.value)
    },
    dataTimes () {
      const {currentGranularity} = this
      const displayFormatter = currentGranularity ? currentGranularity.displayFormatter : ''
      return Object.keys(this.dataMap).map((dataTime, index) => ({
        value: index,
        label: new Date(dataTime).format(displayFormatter),
        id: dataTime
      }))
    },
    reverseDataTimes () {
      return [...this.dataTimes].reverse()
    },
    valueKey: () => null,
    features () {
      const dataTimeObj = this.dataTimes[this.dataIndex]

      if (!dataTimeObj) {
        return null
      }

      const dataTime = dataTimeObj.id
      const data = this.dataMap[dataTime]
      const {profileSiteMap, valueKey} = this
      // levels: this.AQI_LEVEL_MAP[valueKey]

      return this.filterSites.reduce((list, filterSite) => {
        const siteId = filterSite.id
        const siteData = data[siteId]

        if (siteData) {
          const value = siteData[valueKey]

          if (Number.isFinite(value)) {
            const site = profileSiteMap[siteId]

            list.push({
              type: 'Feature',
              properties: {
                value
              },
              geometry: {
                type: 'Point',
                coordinates: [site.longitude, site.latitude]
              }
            })
          }
        }

        return list
      }, [])
    },
    levels () {
      const levels = this.AQI_LEVEL_MAP[this.valueKey]
      const values = this.features.map(feature => feature.properties.value)
      const min = Math.min(...values, 0)
      const max = Math.max(...values, 0)
      // const minLevelIndex = levels.findIndex(level => min <= level) - 1
      const minLevelIndex = 0
      const maxLevelIndex = levels.findIndex(level => max <= level) + 1
      const sliceLevels = levels.slice(minLevelIndex, maxLevelIndex)
      const {length} = sliceLevels
      const colors = this.colors.slice(minLevelIndex, maxLevelIndex)

      const firstLevel = sliceLevels[0]
      const width = canvas.width = 1
      const height = canvas.height = sliceLevels[length - 1] - firstLevel
      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, width, height)
      const lg = ctx.createLinearGradient(0, 0, 0, height)
      const diff = sliceLevels[length - 1] - firstLevel
      const intervals = sliceLevels.map(level => (level - firstLevel) / diff)

      colors.forEach((color, index) => {
        lg.addColorStop(intervals[index], color)
      })

      ctx.fillStyle = lg
      ctx.fillRect(0, 0, width, height)
      const imageData = ctx.getImageData(0, 0, width, height).data
      const imageDataLen = imageData.length
      const sourceColors = []

      for (let i = 0; i < imageDataLen; i += 4) {
        sourceColors.push({
          fill: `rgb(${imageData[i]},${imageData[i + 1]},${imageData[i + 2]})`
        })
      }

      if (sliceLevels.length === 2) {
        sliceLevels[0] = min
        sliceLevels[1] = max
      }

      const newLevels = []
      const newColors = []
      const newFirstLevel = sliceLevels[0]
      const newDiff = sliceLevels[length - 1] - newFirstLevel
      const realColorsLen = sourceColors.length - 1
      const colorIndexPercentages = []

      for (let i = 1; i < length; i++) {
        let begin = sliceLevels[i - 1]
        let end = sliceLevels[i]
        const interval = (end - begin) / 5

        for (let j = 0; j < 5; j++) {
          const levelNum = begin + interval * j
          newLevels.push(levelNum)
          const index = Math.round((levelNum - newFirstLevel) / newDiff * realColorsLen)
          colorIndexPercentages.push(index)
          newColors.push(sourceColors[index])
        }
      }

      newLevels.push(sliceLevels[length - 1])
      newColors.push(sourceColors[realColorsLen])
      colorIndexPercentages.push(100)

      return {
        colors: newColors,
        levels: newLevels,
        colorIndexPercentages
      }
    },
    geoJson () {
      const {dataIndex} = this

      if (dataIndex === -1) {
        return null
      }

      const {features} = this

      if (!features || features.length === 0) {
        return
      }

      let {levels: {levels, colors}} = this
      const {boundaries: [boundaries]} = this.system
      const siteCoords = features.map(feature => feature.geometry.coordinates)
      const line = turf.lineString(boundaries.concat(siteCoords))
      const bbox = turf.bbox(line)
      const bboxPolygon = turf.bboxPolygon(bbox)
      const minMax = bboxPolygon.bbox
      features.push({
        type: 'Feature',
        properties: {
          // value: levels[0] - 1
          value: 0
        },
        geometry: {
          type: 'Point',
          coordinates: [minMax[0], minMax[3]]
        }
      }, {
        type: 'Feature',
        properties: {
          // value: levels[0] - 1
          value: 0
        },
        geometry: {
          type: 'Point',
          coordinates: [minMax[2], minMax[3]]
        }
      }, {
        type: 'Feature',
        properties: {
          // value: levels[0] - 1
          value: 0
        },
        geometry: {
          type: 'Point',
          coordinates: [minMax[2], minMax[1]]
        }
      }, {
        type: 'Feature',
        properties: {
          // value: levels[0] - 1
          value: 0
        },
        geometry: {
          type: 'Point',
          coordinates: [minMax[0], minMax[1]]
        }
      })

      const points = turf.featureCollection(features)
      const grid = turf.interpolate(points, 0.005, {
        gridType: 'points',
        property: 'value',
        units: 'degrees',
        weight: 5
      })
      grid.features.map(feature => (feature.properties.value = feature.properties.value.toFixed(2)))
      const isobandsOption = {
        zProperty: 'value',
        commonProperties: {
          'fill-opacity': 0.8
        },
        breaksProperties: colors
      }
      const isobands = turf.flatten(turf.isobands(grid, levels, isobandsOption))
      const boundaryFeatures = this.boundaries.features
      let isobandFeatures = isobands.features

      const newFeatures = []

      isobandFeatures.forEach(layer1 => {
        boundaryFeatures.forEach(layer2 => {
          let intersection = null

          try {
            intersection = turf.intersect(layer1, layer2)
          } catch (e) {
            layer1 = turf.buffer(layer1, 0)
            intersection = turf.intersect(layer1, layer2)
          }

          if (intersection != null) {
            intersection.properties = layer1.properties
            intersection.id = Math.random() * 100000
            newFeatures.push(intersection)
          }
        })
      })

      return Object.freeze(turf.featureCollection(newFeatures))
      // return Object.freeze(isobands)
    },
    geoJsonStyle: () => ({properties}) => ({
      fillColor: properties.fill,
      fillOpacity: properties['fill-opacity'],
      stroke: true,
      weight: 1,
      opacity: properties['fill-opacity'],
      color: properties.fill,
      // opacity: 0,
      // color: '#ffffff',
      fill: true
    }),
    gifFilename: () => '',
    legendColors () {
      const {colors, colorIndexPercentages} = this.levels
      return colorIndexPercentages.map((percentage, index) => `${colors[index].fill} ${percentage}%`).join(',')
    }
  },
  methods: {
    formatData (dataList) {

    },
    formatAQIData (aqiDataList) {
      if (aqiDataList.length === 0) {
        this.dataMap = Object.freeze({})
        this.dataTime = -1
        return
      }

      const {step, unit, complete, formatter} = this.currentGranularity
      let {beginTime, endTime} = this.currentQuery
      beginTime = new Date(beginTime + complete)
      const endTS = Math.min(new Date(endTime + complete).getTime(), new Date(aqiDataList[aqiDataList.length - 1].dataTime + complete).getTime())
      const dataMap = {}

      for (; beginTime.getTime() <= endTS; beginTime[`set${unit}`](beginTime[`get${unit}`]() + step)) {
        const dataTime = beginTime.format(formatter)
        dataMap[dataTime] = {}
      }

      this.dataMap = Object.freeze(aqiDataList.reduce((map, data) => {
        const siteData = map[data.dataTime]
        siteData[data.siteId] = data.pollutantData.reduce((map, data) => {
          let {rootFactorId, factorId} = data

          if (rootFactorId === 'H8O3') {
            factorId = '1006-8H'
          } else if (/^PM(10|2\.5)_24H$/i.test(rootFactorId)) {
            factorId = factorId + '-24H'
          }

          map[`${factorId}-value`] = data.value
          map[`${factorId}-iaqi`] = data.iaqi

          return map
        }, {
          aqi: data.aqi
        })

        return map
      }, dataMap))

      this.dataIndex = this.dataTimes.length - 1
    },
    clearPlayTimer () {
      clearTimeout(this.playTimer)
    },
    clearRecordScreenTimer () {
      clearTimeout(this.recordScreenTimer)
    },
    getSpaceMap () {
      return this.$refs['space-map']
    },
    onRecordScreenBtnClick () {
      this.changeLoadStatus(true)
      const bakDataIndex = this.dataIndex
      const mapWrapper = this.getSpaceMap().getMapWrapper()
      const svg = mapWrapper.querySelector('svg.leaflet-zoom-animated')
      const viewBoxes = svg.getAttribute('viewBox').split(/\s+/)
      svg.setAttribute('viewBox', `0 0 ${viewBoxes[2]} ${viewBoxes[3]}`)
      svg.style.transform = 'translate3d(0px, 0px, 0px)'

      const {gifFilename} = this
      const gif = new GIF({
        workers: 2,
        quality: 15,
        width: mapWrapper.offsetWidth * GIF_SIZE_RATIO,
        height: mapWrapper.offsetHeight * GIF_SIZE_RATIO,
        workerScript: '/static/vendor/gifJS/gif.worker.js'
      })

      gif.on('finished', blob => {
        if (navigator.msSaveOrOpenBlob) {
          navigator.msSaveOrOpenBlob(blob, gifFilename)
        } else {
          let url = URL.createObjectURL(blob)
          let link = document.createElement('a')
          link.download = gifFilename
          link.href = url
          link.click()
          URL.revokeObjectURL(url)
        }

        this.dataIndex = bakDataIndex
        this.changeLoadStatus(false)
      })

      this.capNextFrame(0, mapWrapper, gif)
    },
    capNextFrame (dataIndex, mapWrapper, gif) {
      if (dataIndex >= this.dataTimes.length) {
        gif.render()
        return
      }

      this.dataIndex = dataIndex

      setTimeout(() => {
        html2canvas(mapWrapper, {
          logging: true,
          allowTaint: true,
          useCORS: true,
          scale: 1
        }).then(canvas => {
          let tmpCanvas = document.createElement('canvas')
          let tmpCtx = tmpCanvas.getContext('2d')
          let {options} = gif
          let width = options.width
          let height = options.height

          tmpCanvas.width = width
          tmpCanvas.height = height

          tmpCtx.drawImage(
            canvas, // 规定要使用的图像、画布或视频。
            0, 0, // 开始剪切的 x 坐标位置。
            canvas.width, canvas.height, // 被剪切图像的高度。
            0, 0, // 在画布上放置图像的 x 、y坐标位置。
            width, height // 要使用的图像的宽度、高度
          )

          gif.addFrame(tmpCanvas, {delay: 300})
          this.capNextFrame(dataIndex + 1, mapWrapper, gif)
        })
      }, 0)
    }
  },
  watch: {
    viewRect (viewRect) {
      this.$refs['space-map'].fitView(viewRect)
    }
  },
  beforeDestroy () {
    this.clearPlayTimer()
  }
}



// WEBPACK FOOTER //
// ./src/views/BaseContourSurface.js